import { useLiveQuery } from 'dexie-react-hooks'
import { createRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { reloadOption } from '../../app/chunks'
import type { Attachment, Me, Message } from '../../api/types'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Icon } from '../../components/Icon'
import { channelMessages, db, type LocalChannel, type OutboxRow } from '../../db/db'
import { useUserMap } from '../../hooks/useUsers'
import { botAcceptsMessages, botConversationNote, botDisplayName } from '../../lib/bots'
import { canModerate as canModerateChannel } from '../../lib/channels'
import { isImageType } from '../../lib/files'
import { canPostIn, canReplyPrivately, isRestricted, restrictedNote, roleCanPost } from '../../lib/posting'
import { toast } from '../../state/toasts'
import { mergeOutbox } from '../../sync/outboxMerge'
import { ChannelDetailsDrawer } from '../channels/ChannelDetailsDrawer'
import { Composer } from '../composer/Composer'
import type { ForwardDialog } from '../forward/ForwardDialog'
import { loadForwardDialog } from '../forward/loadForwardDialog'
import { ConnectionBanner } from '../../components/ConnectionBanner'
import { AdminBanner } from './AdminBanner'
import { ConversationContext, type ConversationContextValue } from './ConversationContext'
import { ConversationHeader } from './ConversationHeader'
import { QuietPartnerNote } from './QuietPartnerNote'
import type { EditFormHandle } from './EditForm'
import { GestureTip } from './GestureTip'
import type { Lightbox } from './Lightbox'
import { deleteMessage } from './messageActions'
import { MessageList } from './MessageList'
import { loadPhotoViewer } from './photoViewer'
import { PinnedDrawer } from './PinnedDrawer'
import { TypingIndicator } from './TypingIndicator'
import { useFocusedChannel } from './useReadTracking'
import { useConsumeJump } from './useConsumeJump'
import { formatClock, formatDayLabel } from '../../lib/format'
import { userName } from '../../lib/channels'

const NO_MESSAGES: Message[] = []
const NO_OUTBOX: OutboxRow[] = []

export function ConversationPane({ channel, me }: { channel: LocalChannel; me: Me }) {
  const users = useUserMap()
  const messages = useLiveQuery(() => channelMessages(channel.id).toArray(), [channel.id], NO_MESSAGES)
  const outbox = useLiveQuery(() => db.outbox.where('channelId').equals(channel.id).toArray(), [channel.id], NO_OUTBOX)
  const [searchParams] = useSearchParams()
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [editing, setEditing] = useState<ConversationContextValue['editing']>(null)
  const [lightbox, setLightbox] = useState<Attachment | null>(null)
  const [Viewer, setViewer] = useState<typeof Lightbox | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Message | null>(null)
  const [forwarding, setForwarding] = useState<Message | null>(null)
  const [Forward, setForward] = useState<typeof ForwardDialog | null>(null)
  const [panel, setPanel] = useState<'details' | 'pinned' | null>(null)
  // Which part of the details to land on: the quiet chip sends people straight to their notification settings.
  const [detailsSection, setDetailsSection] = useState<'notifications' | undefined>(undefined)
  const [jump, setJump] = useState<{ id: string; seq?: number; nonce: number } | null>(() => {
    const id = searchParams.get('m')
    const seq = Number(searchParams.get('seq'))
    return id ? { id, seq: Number.isFinite(seq) && seq > 0 ? seq : undefined, nonce: Date.now() } : null
  })

  useFocusedChannel(channel.id)

  useEffect(() => {
    void db.channels.update(channel.id, { lastViewedAt: new Date().toISOString() })
  }, [channel.id])

  useConsumeJump()

  const timeline = useMemo(() => mergeOutbox(messages, outbox, me.id), [messages, outbox, me.id])
  const messagesById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages])
  // The viewer moves through the conversation's photos stored on this device, oldest first.
  const viewing = lightbox !== null
  const photos = useMemo(
    () => (viewing ? messages.filter((m) => !m.deletedAt).flatMap((m) => m.attachments.filter((a) => isImageType(a.contentType))) : []),
    [viewing, messages],
  )
  // The viewer's header: who sent the photo, and when.
  const describePhoto = (photo: Attachment) => {
    const from = messages.find((m) => m.attachments.some((a) => a.id === photo.id))
    if (!from) return null
    return {
      title: from.authorId === me.id ? 'You' : userName(users, from.authorId),
      detail: `${formatDayLabel(from.createdAt)}, ${formatClock(from.createdAt)}`,
    }
  }
  // A photo deleted while it shows closes the viewer.
  if (lightbox && !photos.some((photo) => photo.id === lightbox.id)) setLightbox(null)

  const jumpToMessage = useCallback((id: string, seq?: number) => setJump({ id, seq, nonce: Date.now() }), [])
  const clearJump = useCallback(() => setJump(null), [])

  const context = useMemo<ConversationContextValue>(
    () => ({
      channel,
      me,
      users,
      canModerate: canModerateChannel(channel, me),
      messagesById,
      startReply: (message) => setReplyTo(message),
      jumpToMessage,
      openAttachment: (attachment) => {
        // The viewer loads the first time a photo is opened; if it can't, the conversation carries on without it.
        void loadPhotoViewer().then(
          (viewer) => {
            setViewer(() => viewer)
            setLightbox(attachment)
          },
          () => toast("Couldn't open the photo viewer", reloadOption(loadPhotoViewer)),
        )
      },
      requestForward: (message) => {
        // Like the photo viewer, the dialog loads the first time it's needed.
        void loadForwardDialog().then(
          (dialog) => {
            setForward(() => dialog)
            setForwarding(message)
          },
          () => toast("Couldn't start forwarding. Try again.", reloadOption(loadForwardDialog)),
        )
      },
      requestDelete: (message) => setPendingDelete(message),
      editing,
      startEdit: (message, returnFocus = null) => {
        // Typed changes are never thrown away: the open edit keeps them until it is saved or cancelled.
        if (editing && (editing.messageId === message.id || editing.form.current?.changed())) {
          setEditing({ ...editing, focusRequests: editing.focusRequests + 1 })
          if (editing.messageId !== message.id) toast('Finish or cancel your current edit first')
          return
        }
        setEditing({ messageId: message.id, returnFocus, form: createRef<EditFormHandle>(), focusRequests: 0 })
      },
      endEdit: (messageId) => setEditing((current) => (current?.messageId === messageId ? null : current)),
    }),
    [channel, me, users, messagesById, jumpToMessage, editing],
  )

  // People write in a bot conversation only when the bot has a webhook to receive it (contract: "Bots").
  const readOnlyBot = channel.type === 'bot_dm' && !botAcceptsMessages(channel)
  // In a restricted channel only owners and posters write; everyone else may reply privately to its bot.
  const readOnlyRestricted = channel.isMember && !channel.archivedAt && isRestricted(channel) && !roleCanPost(channel)
  const readOnly = readOnlyBot || readOnlyRestricted || !channel.isMember || Boolean(channel.archivedAt)
  const restricted = readOnlyRestricted ? restrictedNote(channel, users) : null

  return (
    <ConversationContext.Provider value={context}>
      <ConversationHeader
        onOpenDetails={(section) => {
          setDetailsSection(section)
          setPanel('details')
        }}
        onOpenPinned={() => setPanel('pinned')}
      />
      <ConnectionBanner />
      {!channel.isMember ? <AdminBanner channelId={channel.id} managed={channel.managed} joinable={channel.type === 'channel'} /> : null}
      {channel.archivedAt ? (
        <div className="archived-banner" role="note">
          This {channel.type === 'channel' ? 'channel' : 'conversation'} is archived. Messages are read-only.
        </div>
      ) : null}
      <MessageList timeline={timeline} jump={jump} onJumpHandled={clearJump} />
      {timeline.length > 0 && (canPostIn(channel) || canReplyPrivately(channel)) ? <GestureTip /> : null}
      <TypingIndicator channelId={channel.id} />
      <QuietPartnerNote channel={channel} users={users} meId={me.id} />
      {restricted ? (
        <div className="composer-disabled restricted-note" role="note">
          <Icon name="megaphone" />
          <span>
            <strong>{restricted.title}</strong>
            {restricted.detail ? <span> {restricted.detail}</span> : null}
          </span>
        </div>
      ) : readOnly ? (
        <div className="composer-disabled">
          {readOnlyBot
            ? botConversationNote(botDisplayName(channel, users, me.id))
            : channel.isMember
            ? channel.type === 'channel'
              ? 'Unarchive the channel to send messages.'
              : 'This conversation is archived.'
            : channel.managed?.policy === 'managed_only'
              ? 'Membership of this channel is managed automatically.'
              : // An admin may read any conversation, but joining is for channels: telling them to join a direct
                // message or a group chat asks for something the server will not allow.
                channel.type === 'channel'
                ? 'Join the channel to send messages.'
                : 'Only the people in this conversation can send messages.'}
        </div>
      ) : (
        <Composer replyTo={replyTo} onReplyChange={setReplyTo} timeline={timeline} />
      )}

      <ChannelDetailsDrawer open={panel === 'details'} focus={detailsSection} onClose={() => setPanel(null)} />
      <PinnedDrawer
        open={panel === 'pinned'}
        onClose={() => setPanel(null)}
        onJump={(id, seq) => {
          setPanel(null)
          jumpToMessage(id, seq)
        }}
      />
      {lightbox && Viewer ? (
        <Viewer attachment={lightbox} photos={photos} onChange={setLightbox} onClose={() => setLightbox(null)} describe={describePhoto} />
      ) : null}
      {forwarding && Forward ? <Forward message={forwarding} me={me} onClose={() => setForwarding(null)} /> : null}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete message?"
        confirmLabel="Delete"
        danger
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete) await deleteMessage(pendingDelete.id)
        }}
      >
        <p>This removes the message for everyone in the conversation. This can't be undone.</p>
      </ConfirmDialog>
    </ConversationContext.Provider>
  )
}
